<template>
  <div class="view-wrapper d-flex flex-column">

    <div class="header px-5 py-3 flex-shrink-0">
      <v-row align="center" no-gutters>
        <v-col cols="5">
          <v-list-item v-if="currentClass" class="pa-0">
            <v-avatar size="5vw" class="mr-4 elevation-2"
              v-if="currentClass.professorUser && currentClass.professorUser.userImageSmall">
              <ImageDisplay :src="currentClass.professorUser.userImageSmall" />
            </v-avatar>
            <v-list-item-content>
              <v-list-item-title class="responsive-header-title primary--text text-truncate">
                {{ currentClass.name }}
              </v-list-item-title>
              <v-list-item-subtitle class="responsive-header-subtitle font-weight-bold grey--text text--darken-3">
                {{ currentClass.professorUser ? currentClass.professorUser.displayName : '' }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="responsive-header-subtitle grey--text font-weight-medium">
                {{ $d(new Date(currentClass.startDate), 'time', $i18n.locale) }} -
                {{ $d(new Date(currentClass.endDate), 'time', $i18n.locale) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-col>

        <v-col cols="2" class="d-flex justify-center">
          <v-avatar v-if="academyLogo" @click="navigateTo({ name: 'checkin' })" size="8.5vw" class="elevation-4">
            <ImageDisplay :src="academyLogo" />
          </v-avatar>
        </v-col>

        <v-col cols="5" class="text-right">
          <span v-if="currentTime" class="responsive-clock font-weight-black">
            {{ $d(currentTime, 'time', $i18n.locale) }}
          </span>
        </v-col>
      </v-row>
      <v-divider class="mt-3"></v-divider>
    </div>

    <div class="main-content flex-grow-1 px-4 pt-2">
      <v-card-text class="mt-0 pt-0 fill-height" v-if="currentClass">
        <div class="responsive-subheader py-2 text-uppercase font-weight-black">
          {{ $t('message.in_class') }}: {{ checkins ? checkins.length : 0 }}
        </div>

        <div v-if="checkins" class="checkin-column-flow" ref="checkinContainer">

          <v-card outlined class="checkin-column-item" v-for="(i, index) in checkins" :key="'checkin_' + index">
            <div class="d-flex align-center pa-2 fill-height">

              <v-badge v-if="i.isBirthday" overlap top left color="pink" icon="mdi-cake-variant"
                class="mr-3 birthday-badge">
                <v-avatar size="50" class="initials-avatar">
                  <ImageDisplay v-if="i.image" :src="i.image" />
                  <span v-else class="avatar-initials">{{ getInitials(i.userName) }}</span>
                </v-avatar>
              </v-badge>
              <v-avatar v-else size="50" class="mr-3 initials-avatar">
                <ImageDisplay v-if="i.image" :src="i.image" />
                <span v-else class="avatar-initials">{{ getInitials(i.userName) }}</span>
              </v-avatar>

              <div class="d-flex flex-column justify-center align-start text-left text-truncate"
                style="flex: 1; min-width: 0;">
                <div class="responsive-sidebar-name text-truncate" style="width: 100%;">{{ i.userName }}</div>
                <div class="d-flex align-center" style="height: 20px;">
                  <belt v-if="!i.notBjjUser" :size="10" :belt="i.belt" :show-text="false" />
                </div>
              </div>

              <v-spacer></v-spacer>

              <div class="d-flex align-center status-group-right">
                <div v-if="i.isBirthday || i.isProfessor || i.isAssistantProfessor" class="mr-4 text-right">
                  <div v-if="i.isBirthday" class="pink--text font-weight-black responsive-status-text">
                    {{ $t('message.happy_birthday') }}!
                  </div>
                  <div v-else-if="i.isProfessor || i.isAssistantProfessor"
                    class="primary--text font-weight-black responsive-status-text">
                    <v-icon color="primary" size="1.2vw">mdi-account-star-outline</v-icon> {{ $t('message.professor') }}
                  </div>
                </div>

                <country-flag v-if="i.nationality" size="2vw" :country-code="i.nationality" />
              </div>

            </div>
          </v-card>

        </div>
      </v-card-text>

      <v-card-text class="d-flex flex-column justify-center align-center fill-height" v-else>
        <v-avatar size="20vw" class="elevation-0 mb-6">
          <ImageDisplay :src="academyLogo" />
        </v-avatar>
        <div v-if="nextClass" class="text-center">
          <div class="responsive-next-title primary--text font-weight-bold">
            {{ $t('message.next_class') }}: {{ nextClass.name }}
          </div>
          <div class="responsive-next-sub mt-2 text-h4">
            @ {{ $d(new Date(nextClass.startDate), 'time', $i18n.locale) }}
          </div>
        </div>
      </v-card-text>
    </div>

    <div class="footer d-flex align-center justify-space-between pa-2 flex-shrink-0">
      <div class="left-content d-flex align-center">
        <v-chip color="secondary" label class="responsive-footer-chip" v-if="nextClass">
          <span class="chip-content">
            <b>{{ $t('message.next_class') }}:</b> {{ nextClass.name }}
            <span class="ml-2 font-weight-regular" v-if="nextClass.professorUser">
              w/ {{ nextClass.professorUser.displayName }}
            </span>
            <span class="ml-2 font-weight-regular">
              @ {{ $d(new Date(nextClass.startDate), 'time', $i18n.locale) }}
            </span>
          </span>
        </v-chip>
      </div>

      <div class="right-content d-flex align-center responsive-footer-brand">
        <span class="mr-2"><b>BJJ</b>LINK</span>
        <div style="width: 7vw;">
          <v-img src="https://bjjlink-pub.s3.us-west-2.amazonaws.com/logos/bjjlink-2025-black.png" contain></v-img>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Belt from '@/components/displays/BeltDisplay.vue';
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import CountryFlag from '@/components/displays/CountryFlagDisplay.vue';
import Api from "@/services/Api";

export default {
  name: 'FullScreenCheckinsOnly',
  data() {
    return {
      classLocation: this.$route.query.location,
      academyLogo: null,
      currentClass: null,
      nextClass: null,
      pullInterval: null,
      clockTimer: null,
      currentTime: new Date(),
      checkins: [],
      columnCount: 1, // Calculated based on screen height
    }
  },
  watch: {
    checkins: {
      handler() { this.calculateLayout(); },
      deep: true
    }
  },
  beforeDestroy() {
    if (this.pullInterval) clearInterval(this.pullInterval);
    if (this.clockTimer) clearInterval(this.clockTimer);
    window.removeEventListener('resize', this.calculateLayout);
  },
  created() {
    this.getData();
    this.pullInterval = setInterval(() => this.getData(true), 10000);
  },
  mounted() {
    this.clockTimer = setInterval(this.updateTime, 1000);
    window.addEventListener('resize', this.calculateLayout);
    this.calculateLayout();
  },
  components: { CirclesLoader, CountryFlag, Belt },
  methods: {
    updateTime() { this.currentTime = new Date(); },
    getInitials(name) {
      if (!name) return "";
      const p = name.split(' ');
      return (p[0][0] + (p[1] ? p[1][0] : '')).toUpperCase();
    },
    isBirthday(dob) {
      if (!dob) return false;
      const d = new Date(dob);
      const today = new Date();
      return d.getDate() === today.getDate() && d.getMonth() === today.getMonth();
    },
    calculateLayout() {
      this.$nextTick(() => {
        const container = this.$refs.checkinContainer;
        if (!container || !this.checkins.length) return;

        const availHeight = container.clientHeight;
        // Total row height including margin
        const cardHeight = 78;

        // How many cards fit in one column?
        const itemsPerCol = Math.floor(availHeight / cardHeight);

        // How many columns do we need?
        const needed = Math.ceil(this.checkins.length / itemsPerCol);

        // Cap at 4 columns for a TV landscape view
        this.columnCount = Math.max(1, Math.min(needed, 4));
      });
    },
    getData() {
      const now = new Date();
      let url = "admintablet/academy/" + this.$store.state.tabletManagedAcademy + "/checkins-full-screen" + "?now=" + now.getTime();
      if (this.classLocation && this.classLocation != "")
        url += '&location=' + encodeURI(this.classLocation);
      Api().get(url)
        .then(response => {
          this.academyLogo = response.data.logo;
          this.currentClass = response.data.class;
          this.nextClass = response.data.nextClass;
          if (response.data.checkins) {
            for (let i of response.data.checkins) {
              i.isBirthday = this.isBirthday(i.dob);
            }
          }
          this.checkins = response.data.checkins || [];
        });
    },
  },
}
</script>

<style scoped>
.view-wrapper {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  background-color: #fff;
}

/* HEADER & FOOTER */
.header {
  background: white;
  border-bottom: 1px solid #eee;
}

.footer {
  background: white;
  border-top: 1px solid #eee;
  height: 6vw;
}

.responsive-header-title {
  font-size: 2.2vw !important;
  font-weight: 900;
}

.responsive-header-subtitle {
  font-size: 1.4vw !important;
}

.responsive-clock {
  font-size: 5.5vw !important;
}

/* MULTI-COLUMN FLOW ENGINE */
.checkin-column-flow {
  height: 95%;
  column-fill: auto;
  column-gap: 20px;
  /* Syncs columnCount from JS to CSS */
  column-count: v-bind(columnCount);
}

.checkin-column-item {
  break-inside: avoid;
  display: block;
  width: 100%;
  border-radius: 12px !important;
  background-color: white !important;
  border: 1px solid #eee !important;
  margin-bottom: 10px;
  height: 68px;
  /* Fixed height for consistent multi-column math */
}

/* AVATARS & BADGES */
.initials-avatar {
  background-color: #eee !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.avatar-initials {
  font-size: 1.2vw;
  font-weight: bold;
  color: #555;
}

/* TEXT SCALING */
.responsive-sidebar-name {
  font-size: 1.6vw !important;
  font-weight: 800;
  line-height: 1.1;
}

.responsive-subheader {
  font-size: 1.8vw !important;
  color: #333;
}

.responsive-status-text {
  font-size: 1.1vw !important;
  line-height: 1.2;
}

/* HEADER/FOOTER MISC */
.responsive-next-title {
  font-size: 3.5vw !important;
}

.responsive-footer-chip {
  font-size: 1.4vw !important;
  height: 3.2vw !important;
}

.responsive-footer-brand {
  font-size: 2.8vw !important;
}

.main-content {
  overflow: hidden;
  height: 100%;
}

/* Badge Scaling */
::v-deep .v-badge__badge {
  height: 1.5vw !important;
  width: 1.5vw !important;
  min-width: 1.5vw !important;
  border: 2px solid white !important;
}

::v-deep .v-icon {
  font-size: 1.1vw !important;
}
</style>